/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const SpawnPointCtor = (typeof globalThis !== "undefined" && (globalThis.SpawnPointNode || globalThis.Object3D)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const SPAWN_PROPS = []; // SpawnPoint currently exposes no mutatable properties via Inspector

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class SpawnPointEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    // No props to map yet, but structure is ready for future properties
    SPAWN_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);

          // Default property setting
          const originalDescriptor = this._originalDescriptors.get(prop);
          if (originalDescriptor && originalDescriptor.set) {
            try {
              originalDescriptor.set.call(this.sourceObject, v);
            } catch (e) {
              console.warn(`Failed to call original setter for ${prop}:`, e);
            }
          } else {
            try {
              this.sourceObject[prop] = v;
            } catch (e) {
              // ignore
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class SpawnPointAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const COMPONENT = "spawn-point";
    const pending = (this._pendingUpdates = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      if (Object.keys(pending).length > 0) {
        const current = { ...(el.getAttribute(COMPONENT) || {}) };
        Object.assign(current, pending);
        el.setAttribute(COMPONENT, current);
        Object.keys(pending).forEach(key => delete pending[key]);
      }
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      if (e?.detail?.name === COMPONENT) {
        flush();
      }
    });

    const setComponentProp = (key, val) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(COMPONENT);
      if (!attr) {
        pending[key] = val;
      } else {
        const current = { ...attr };
        current[key] = val;
        el.setAttribute(COMPONENT, current);
      }
    };

    const getComponentProp = key => {
      const el = this.sourceObject?.el;
      if (!el) return pending[key];
      const attr = el.getAttribute(COMPONENT);
      return (attr && attr[key]) ?? pending[key];
    };

    // Map each prop (currently none, but structure ready for future)
    SPAWN_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(prop),
        set: v => setComponentProp(prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of SPAWN_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new SpawnPointAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new SpawnPointEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Main SpawnPointElement class                                               */
/* ────────────────────────────────────────────────────────────────────────── */
export class SpawnPointElement extends ScriptableObject {
  static scriptableType = "spawn-point";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      // A spawn-point is represented by a marker component
      el.setAttribute("spawn-point", "");

      // Apply initial transform properties if any
      if (initial.position) {
        el.object3D.position.copy(initial.position);
      }
      if (initial.rotation) {
        el.object3D.rotation.copy(initial.rotation);
      }
      if (initial.scale) {
        el.object3D.scale.copy(initial.scale);
      }

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }
      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createSpawnPointElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public spawn point props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of SPAWN_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of source.children || []) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of SPAWN_PROPS) {
      const v = this[p];
      if (v === undefined) continue;

      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    // Prefer factory if available (handles adopt/constructor nuances)
    const Ctor = this.constructor && typeof this.constructor.create === "function" ? this.constructor : SpawnPointElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new SpawnPointElement(init);

    // Write object3D transforms (component sync for A-Frame to be added later)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Copy remaining fields & children
    return dest.copy(this, recursive);
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* DRY helper: upgrade a plain Editor SpawnPointNode instance to Scriptable  */
/* ────────────────────────────────────────────────────────────────────────── */
function makeEditorSpawnPointNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || SpawnPointCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: SpawnPointElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = SpawnPointElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new SpawnPointEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new SpawnPointEditorAdapter(node, descriptors);
  }

  for (const prop of SPAWN_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
SPAWN_PROPS.forEach(prop => {
  Object.defineProperty(SpawnPointElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createSpawnPointElementEditor(initial = {}) {
  const SpawnPointCtor = globalThis.SpawnPointNode;

  if (typeof SpawnPointCtor !== "function") {
    throw new Error("SpawnPointElementEditor: SpawnPointNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new SpawnPointCtor(editor);
  } catch (e) {
    throw new Error(`SpawnPointElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorSpawnPointNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("spawn-point", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== SpawnPointElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return SpawnPointElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorSpawnPointNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(SpawnPointElement.prototype, Symbol.toStringTag, { value: "SpawnPointElement" });

// Named exports expected by external consumers
export { SpawnPointAFrameAdapter, SpawnPointEditorAdapter };
